import type { CellObject } from "xlsx";

/**
 * 챗봇에 붙인 엑셀 · CSV 를 표(첫 줄 머리글)로 읽는다 — 화면 엑셀 업로드 팝업에 넣을 때 (aibot-v2-attachments 4단계, 2026-10-28).
 * 값은 붙여넣기처럼 글로 바꾼다. 수식 칸은 엑셀이 저장해 둔 결과값, 날짜는 yyyy-MM-dd.
 * 날짜는 cellDates 를 쓰지 않고 서식으로 직접 푼다 — cellDates 는 한국 시간대에서 하루 앞날로 밀린다.
 * 리뷰(2026-10-28) — 오류 칸(#DIV/0!)은 빈칸, 0 채움 · % 서식은 보이는 대로, 숨김 시트는 건너뛴다.
 * 한국어 기본 날짜 서식(번호 27~36 · 50~58)은 SheetJS 가 서식(z) 없이 날짜 글(w)만 주므로 w 모양으로 날짜로 본다
 * 리뷰(2026-10-29) — 시트 범위(!ref)를 다 돌지 않고 값이 있는 칸만 본다(범위만 크게 잡힌 파일에서 브라우저가 멈췄다).
 * 행 · 칸 상한은 서버 표 읽기와 같다. "A"000 처럼 글자가 붙은 0 채움 서식도 보이는 대로
 */
const MAX_ROWS = 50_000;
const MAX_COLS = 200;

/** 0 채움 서식인가 — 따옴표 · \ 글자를 빼고 0 만 남고 0 채움이면(00000 · "A"000 · "0000"0) */
const zeroPadded = (fmt: string) => {
  const digits = fmt.replace(/"[^"]*"|\\./g, "");
  return /^0+$/.test(digits) && (digits.length > 1 || digits !== fmt);
};

export async function readTableFile(
  file: Blob,
  name: string
): Promise<{ table: string[][]; sheet: string; sheets: number }> {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const book = name.toLowerCase().endsWith(".csv")
    ? XLSX.read(decodeCsv(buf), { type: "string", raw: true })
    : XLSX.read(buf, { type: "array", cellNF: true });
  const date1904 = !!book.Workbook?.WBProps?.date1904;
  const cellText = (cell: CellObject | undefined): string => {
    if (!cell || cell.v == null || cell.t === "e") return "";
    if (cell.t === "n" && typeof cell.v === "number") {
      const fmt = cell.z ?? (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(cell.w ?? "") ? "m/d/yy" : undefined);
      if (typeof fmt === "string" && XLSX.SSF.is_date(fmt)) {
        /* 시각만 있는 칸은 보이는 대로 */
        if (cell.v >= 0 && cell.v < 1) return (cell.w ?? "").trim();
        const d = XLSX.SSF.parse_date_code(cell.v, { date1904 });
        if (!d) return "";
        const p = (n: number) => String(n).padStart(2, "0");
        return `${d.y}-${p(d.m)}-${p(d.d)}`;
      }
      /* 00123 같은 0 채움 코드 · 15% — 보이는 글 그대로 */
      if (typeof fmt === "string" && (fmt.includes("%") || zeroPadded(fmt))) return (cell.w ?? "").trim();
      /* 수식 결과의 부동소수 꼬리(0.30000000000000004)를 걷는다 */
      return String(Number(cell.v.toPrecision(15)));
    }
    return String(cell.v).trim();
  };
  for (const [i, sheet] of book.SheetNames.entries()) {
    const ws = book.Sheets[sheet];
    if (book.Workbook?.Sheets?.[i]?.Hidden || !ws["!ref"]) continue;
    const start = XLSX.utils.decode_range(ws["!ref"]).s.c;
    const byRow = new Map<number, Map<number, string>>();
    let lastCol = -1;
    for (const key of Object.keys(ws)) {
      if (key.startsWith("!")) continue;
      const text = cellText(ws[key] as CellObject);
      if (text === "") continue;
      const { r, c } = XLSX.utils.decode_cell(key);
      if (c < start) continue;
      if (c - start >= MAX_COLS) throw new Error(`칸이 너무 많습니다(최대 ${MAX_COLS}칸).`);
      let row = byRow.get(r);
      if (!row) {
        if (byRow.size >= MAX_ROWS) throw new Error(`행이 너무 많습니다(최대 ${MAX_ROWS.toLocaleString()}행).`);
        byRow.set(r, (row = new Map()));
      }
      row.set(c, text);
      lastCol = Math.max(lastCol, c);
    }
    const table = [...byRow.keys()]
      .sort((a, b) => a - b)
      .map((r) => Array.from({ length: lastCol - start + 1 }, (_, i) => byRow.get(r)?.get(start + i) ?? ""));
    if (table.length > 0) return { table, sheet, sheets: book.SheetNames.length };
  }
  throw new Error("파일에 내용이 없습니다.");
}

/** CSV 는 UTF-8 이 아니면 한글 윈도(MS949)로 본다 */
function decodeCsv(buf: ArrayBuffer): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buf).replace(/^﻿/, "");
  } catch {
    return new TextDecoder("euc-kr").decode(buf);
  }
}
